// tslint:disable:max-line-length
import * as lodash from "lodash";
import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    QueryList,
    Renderer, ViewChild,
    ViewChildren
} from "@angular/core";
import { DatePipe } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import {Calendar, SelectItem} from "primeng/primeng";
import { Observable, Subject, Subscription } from "rxjs";

import {
    LOG_LEVEL_IDS,
    TIME_RANGE_MEASURES,
    IUnityProxyLogLevel,
    IUnityProxyTimeRange,
    TCITenantSelectorType,
    IUnityProxyQueryResponse,
    QUERY_STYLES,
    TIME_RANGE_IDS,
    IUnityProxyFetchHistoricalResponse,
    IUnityProxyFetchTailResponse,
    IUnityProxyLogsQueryRequest,
    IUnityProxyLogEntry,
    TCIAppMessaging,
    TCI_ENVIRONMENT_VAR_TYPE,
    ITCIEnvVars,
    strEnum, BASE_CONSTANTS
} from "@tci/common";
import { NgEnvVarsService } from "../../../ng-shared/ng-services/ng-env-vars.service";
import { NgToastrService } from "../../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { AbstractTCITenantComponent } from "../../../ng-tenant/tci-tenant-component";
import { BaseAppDetailMenuComponentInput, BaseAppDetailMenuComponentOutput } from "../../ng-app-details-menu";
import { BaseAppDetailLogsService } from "./base-app-detail-logs.service";
import * as moment from "moment";

const LOG_MODES = strEnum([
    "realtime_active",
    "realtime_inactive",
    "historical"
]);

type LOG_MODES = keyof typeof LOG_MODES;

const LOG_VIEW_STYLES = strEnum<string>([
    "raw",
    "table"
]);

type LOG_VIEW_STYLES = keyof typeof LOG_VIEW_STYLES;

const LOG_CATEGORIES = strEnum<string>([
    "buildtime",
    "runtime"
]);
type LOG_CATEGORIES = keyof typeof LOG_CATEGORIES;
const SUBSCRIPTION_KEYS = strEnum<string>([
    "createQuery",
    "createQueryForDownload",
    "fetchLogs",
    "fetchLogsForDownload",
    "delayedFetchLogs"
]);
type SUBSCRIPTION_KEYS = keyof typeof SUBSCRIPTION_KEYS;

   /*
    * PrimeNg Calendar component
    * Bug: https://github.com/primefaces/primeng/issues/2229
    * Fix: https://github.com/mitalikhundiwala/primeng/commit/0f3e3bb6587cd0ba53fd8419b23d61adb954345d
    */
Calendar.prototype.formatTime = function (date) {
    if (!date) {
        return "";
    }
    let output = "";
    // since hours is being used for formatting and so changing, storing original value for comparision
    const originalHours = date.getHours();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    // if (this.hourFormat === "12" && hours > 11 && hours !== 12) {
    if (this.hourFormat === "12" && originalHours > 11 && originalHours !== 12) {
        hours -= 12;
    }
    this.pm = originalHours > 11; // Also updating AM/PM flag to reflect change in dropdown.
    output += (hours < 10) ? "0" + hours : hours;
    output += ":";
    output += (minutes < 10) ? "0" + minutes : minutes;
    if (this.showSeconds) {
        output += ":";
        output += (seconds < 10) ? "0" + seconds : seconds;
    }
    if (this.hourFormat === "12") {
        // output += hours > 11 ? " PM" : " AM";
        output += originalHours > 11 ? " PM" : " AM";
    }
    return output;
};


const now = new Date();

@Component({
    selector: TCITenantSelectorType.TCI_APP_DETAIL_LOGS.toString(),
    templateUrl: "./base-app-detail-logs.component.html",
    styleUrls: ["./base-app-detail-logs.component.less"]
})
export class BaseAppDetailLogsComponent<E, I extends BaseAppDetailMenuComponentInput, O extends BaseAppDetailMenuComponentOutput<E>>
    extends AbstractTCITenantComponent<E, I, O> implements OnInit, OnDestroy, AfterViewInit {

    public static readonly REALTIME_LOGS_PER_PAGE = 30;
    public static readonly LOGS_PER_PAGE = 30;
    public static readonly DEFAULT_REALTIME_LOGS_INTERVAL = 5000;
    public static readonly DEFAULT_HISTORICAL_LOGS_INTERVAL = 1000;
    public static readonly DEFAULT_TIME_RANGE_ID = TIME_RANGE_IDS["1h"];
    public static readonly LANG_LOG = "NG_APP_LOGS.";
    public static readonly LAST = "LAST";

    private static logsListElement: Element;

    @ViewChild("logsView") logsView: ElementRef;
    @ViewChildren("logMessageCell") logMessages: QueryList<any>;
    /* bindables are properties that are referenced in the template */
    /********** start bindables **********/
    private readonly baseFixedTimeRange: IUnityProxyTimeRange[] = [
        {
            id: TIME_RANGE_IDS["5m"],
            label: `5 ${this.translateService.instant(BaseAppDetailLogsComponent.LANG_LOG + "TIME_MINS")}`,
            value: 5 * 60 * 1000,
            measure: TIME_RANGE_MEASURES.TIME_MINS
        },
        {
            id: TIME_RANGE_IDS["30m"],
            label: `30 ${this.translateService.instant(BaseAppDetailLogsComponent.LANG_LOG + "TIME_MINS")}`,
            value: 30 * 60 * 1000,
            measure: TIME_RANGE_MEASURES.TIME_MINS
        },
        {
            id: TIME_RANGE_IDS["1h"],
            label: `1 ${this.translateService.instant(BaseAppDetailLogsComponent.LANG_LOG + "TIME_HOUR")}`,
            value: 1 * 60 * 60 * 1000,
            measure: TIME_RANGE_MEASURES.TIME_HOUR
        },
        {
            id: TIME_RANGE_IDS["12h"],
            label: `12 ${this.translateService.instant(BaseAppDetailLogsComponent.LANG_LOG + "TIME_HOURS")}`,
            value: 12 * 60 * 60 * 1000,
            measure: TIME_RANGE_MEASURES.TIME_HOURS
        }
    ];
    private readonly fixedTimeRanges: IUnityProxyTimeRange[] = this.baseFixedTimeRange.concat([
        {
            id: TIME_RANGE_IDS["24h"],
            label: `24 ${this.translateService.instant(BaseAppDetailLogsComponent.LANG_LOG + "TIME_HOURS")}`,
            value: 24 * 60 * 60 * 1000,
            measure: TIME_RANGE_MEASURES.TIME_HOURS
        },
        {
            id: TIME_RANGE_IDS["7d"],
            label: `7 ${this.translateService.instant(BaseAppDetailLogsComponent.LANG_LOG + "TIME_DAYS")}`,
            value: 7 * 24 * 60 * 60 * 1000,
            measure: TIME_RANGE_MEASURES.TIME_DAYS
        },
        {
            id: TIME_RANGE_IDS["30d"],
            label: `30 ${this.translateService.instant(BaseAppDetailLogsComponent.LANG_LOG + "TIME_DAYS")}`,
            value: 30 * 24 * 60 * 60 * 1000,
            measure: TIME_RANGE_MEASURES.TIME_DAYS
        }
    ]);
    private readonly filterTimeRange: IUnityProxyTimeRange[] = this.baseFixedTimeRange.concat(
        {
            id: TIME_RANGE_IDS.custom,
            label: "Custom",
            value: 0,
            measure: null
        }
    );
    private readonly filterLogLevels: IUnityProxyLogLevel[] = [
        {
            id: LOG_LEVEL_IDS.ERROR,
            label: "Error",
            value: false,
            status: "active"
        },
        {
            id: LOG_LEVEL_IDS.WARN,
            label: "Warning",
            value: false,
            status: "active"
        },
        {
            id: LOG_LEVEL_IDS.INFO,
            label: "Info",
            value: false,
            status: "active"
        },
        {
            id: LOG_LEVEL_IDS.DEBUG,
            label: "Debug",
            value: false,
            status: "active"
        }
    ];
    private readonly logViewStyles: SelectItem[] = [
        {
            label: "Table",
            value: LOG_VIEW_STYLES.table
        },
        {
            label: "Raw",
            value: LOG_VIEW_STYLES.raw
        }
    ];
    private logMode: LOG_MODES = LOG_MODES.realtime_inactive;
    private isBuildLogsDisabled = true;
    private buildLogsDisabled = true;
    private endDateTime = now;
    private isLoadingLogs = false;
    private isLazyLoading = false;
    private isPreparingDownload = false;
    private keywordFilter: string;
    private loadingText = this.translateService.instant("NG_APP_LOGS.LOADING");
    private logEntries: IUnityProxyLogEntry[] = [];
    private logLevelFilter: IUnityProxyLogLevel;
    private isRealtimeActive = this.logMode === LOG_MODES.realtime_active;
    private logViewStyle: LOG_VIEW_STYLES = LOG_VIEW_STYLES.table;
    private historicalLogsCount = 0;
    private noMoreLogs = false;
    private selectedFixedTimeRange: IUnityProxyTimeRange = this.fixedTimeRanges[0];
    private selectedLogLevels: LOG_LEVEL_IDS[] = [];
    private selectedTimeRangeType = "fixed";
    private startDateTime = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    private timeRangeFilter: IUnityProxyTimeRange = lodash.find(this.filterTimeRange, {
        id: TIME_RANGE_IDS["5m"]
    });
    private downloadDialogOpen = false;
    private logsTableHeight = 600;
    /********** end bindables **********/

    private appId: string;
    private queryId: string;
    private initialized = false;
    private realtimeQuery: IUnityProxyQueryResponse;
    private historicalQuery: IUnityProxyQueryResponse;
    private origLogEntries: IUnityProxyLogEntry[] = [];
    private logCategory: LOG_CATEGORIES = LOG_CATEGORIES.runtime;
    private loadingCustom = false;
    private loadingRealTime = false;
    private custom = {
        start: null,
        end: null
    };
    private logsListScrolled = false;
    private wasCustomTimeRange = false;
    private isCustomTimeRange = false;
    private isDownloading = false;
    private downloadPercentage = 0;
    private downloadStatusMsg = this.translateService.instant("NG_APP_LOGS.INITIATING_DOWNLOAD");
    private showLogNotification = false;
    private existPrevLogs = false;
    private toggleActive = false;
    private switchToggleData = {
        isOn: this.isRealtimeActive,
        switchToggleClass: this.isRealtimeActive ? "switch-on" : ""
    };
    private lastTimeRangeId: TIME_RANGE_IDS = null;
    private page = 0;
    private fetchSize = 30;
    private newMessagesCount = 0;
    private fetchLogsTimeout = 1000; // milliseconds
    private historicalQueryTries = 1;
    private prevOffsetCount = 0;
    private logsSummary = "";
    private numberOfDisplayed = {
        displayed: this.historicalLogsCount,
        total: this.historicalLogsCount
    };
    private logsContentForDownload = [];
    private tciLogLimitDownload;
    private tciLogLimitUI;
    private tciLogDownloadFetchSize;
    private downloadSize = "";
    private tciLogDownloadSizeLimit = "";
    private realtimeLogStartTime: Date;
    private logsFetchAttempts = 0;
    private logsFetchAttemptsLimit = 40;
    private realtimeSubscriptions: { [key: string]: Subscription } = {};
    private historicalSubscriptions: { [key: string]: Subscription } = {};
    private infiniteScrollingSubject: Subject<any> = new Subject<any>();
    private noLogs = false;

    constructor(
        private appMessinging: TCIAppMessaging,
        private datePipe: DatePipe,
        private envVars: NgEnvVarsService,
        private logsService: BaseAppDetailLogsService,
        private renderer: Renderer,
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private toastr: NgToastrService
    ) {
        super();
    }

    ngOnInit(): void {
        this.appId = this.input.application.id;

        this
            .envVars
            .getEnvVar(
                TCI_ENVIRONMENT_VAR_TYPE.DISABLED_BUILD_LOGS,
                TCI_ENVIRONMENT_VAR_TYPE.WEB_SERVER_DATE_TIME_FORMAT,
                TCI_ENVIRONMENT_VAR_TYPE.TCI_LOG_DOWNLOAD_SIZE_LIMIT,
                TCI_ENVIRONMENT_VAR_TYPE.TCI_LOG_LIMIT_UI,
                TCI_ENVIRONMENT_VAR_TYPE.TCI_LOG_LIMIT_DOWNLOAD,
                TCI_ENVIRONMENT_VAR_TYPE.TCI_LOG_DOWNLOAD_FETCH_SIZE
            )
            .subscribe((envVars: ITCIEnvVars<any>) => {
                this.isBuildLogsDisabled = envVars[TCI_ENVIRONMENT_VAR_TYPE.DISABLED_BUILD_LOGS] as boolean || false;
                this.tciLogDownloadSizeLimit = envVars[TCI_ENVIRONMENT_VAR_TYPE.TCI_LOG_DOWNLOAD_SIZE_LIMIT];
                this.tciLogLimitUI = parseInt(envVars[TCI_ENVIRONMENT_VAR_TYPE.TCI_LOG_LIMIT_UI], 0);
                this.tciLogLimitDownload = parseInt(envVars[TCI_ENVIRONMENT_VAR_TYPE.TCI_LOG_LIMIT_DOWNLOAD], 0);
                this.tciLogDownloadFetchSize = parseInt(envVars[TCI_ENVIRONMENT_VAR_TYPE.TCI_LOG_DOWNLOAD_FETCH_SIZE], 0);
            });

        this
            .infiniteScrollingSubject
            .debounceTime(100)
            .subscribe(() => this.lazyLoadLogs());
    }

    ngOnDestroy(): void {
        this.unsubscribeAll();
        this.logsService.deleteCurrentHistoricalLogsQuery();
        this.infiniteScrollingSubject.unsubscribe();
    }

    ngAfterViewInit(): void {
        BaseAppDetailLogsComponent.logsListElement = this
            .logsView
            .nativeElement
            .querySelector(".ui-datascroller-content");
        this
            .renderer
            .listen(BaseAppDetailLogsComponent.logsListElement, "scroll", () => {
                this.logsListScrolled = BaseAppDetailLogsComponent.logsListElement.scrollTop > 0;
                if (!this.logsListScrolled) {
                    this.newMessagesCount = 0;
                }
            });

        this
            .logMessages
            .changes
            .subscribe(
                (list: QueryList<any>) => {
                    list.forEach((el: ElementRef) => {
                        if (
                            !el.nativeElement.classList.contains("checked-for-expandable") &&
                            el.nativeElement.clientWidth !== el.nativeElement.scrollWidth
                        ) {
                            el.nativeElement.classList.add("expandable", "checked-for-expandable");
                        }
                    });
                }
            );
    }

    private initialize(): void {
        this.initialized = true;
        this.queryByFixedTimeRange(lodash.find(this.filterTimeRange, {
            id: TIME_RANGE_IDS["5m"]
        }));
    }

    private activateHistorical(timeRangeId: TIME_RANGE_IDS): void {

        this.constructLogsSummary(timeRangeId);

        if (timeRangeId === TIME_RANGE_IDS.custom) {
            this.lastTimeRangeId = timeRangeId; // to display the custom log block
            this.logEntries = []; // empty the log items while showing the loader
            this.getCustomLogs();
        } else {
            this.getHistoricalLogs();
        }
    }

    private bytesToSize(bytes: number, decimals: number): string {
        if (bytes === 0) {
            return "0 Byte";
        }

        const sizes = [
            "Bytes",
            "KB",
            "MB",
            "GB",
            "TB"
        ];
        const k = 1000;
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        const x = (bytes / Math.pow(k, i));

        return +x.toFixed(decimals) + " " + sizes[i];
    }

    private cancelDownload(): void {
        this.downloadDialogOpen = false;
    }

    private changeSelectedFixedTimeRange(event): void {
        this.selectedFixedTimeRange = lodash.find(this.fixedTimeRanges, (timeRange: IUnityProxyTimeRange) => {
            return timeRange.value === event.value;
        });
    }

    private clearFilter(): void {
        this.keywordFilter = "";
        this.logEntries = this.origLogEntries.slice();
        this.noLogs = this.logEntries.length === 0;
    }

    private clearItems(): void {
        this.origLogEntries = [];
        this.logEntries = [];
        this.page = 0;
        if (this.logMode === LOG_MODES.realtime_active) {
            this.fetchSize = BaseAppDetailLogsComponent.REALTIME_LOGS_PER_PAGE;
        } else if (this.logMode === LOG_MODES.historical) {
            this.fetchSize = BaseAppDetailLogsComponent.LOGS_PER_PAGE;
        }
    }

    private constructLogsSummary(timeRangeId: TIME_RANGE_IDS) {
        this.logsSummary = `${this.translateService.instant(this.historicalLogsCount === 1 ?
            "NG_APP_LOGS.RESULT" :
            "NG_APP_LOGS.RESULTS")} ${this.translateService.instant("NG_APP_LOGS.DISPLAYED_FROM")} `;

        if (timeRangeId !== TIME_RANGE_IDS.custom) {
            if (this.logMode !== LOG_MODES.realtime_inactive) {
                this.logsSummary += ` <b>${this.tooltip(timeRangeId)}</b> `;
            }
        } else {
            if (this.startDateTime) {
                this.logsSummary += ` <b>${this.datePipe.transform(
                    this.startDateTime,
                    `${BASE_CONSTANTS.TCI_DATE_TIME_FORMAT}`
                )}`;
            }

            if (this.endDateTime) {
                this.logsSummary += `</b> ${this.translateService.instant("NG_APP_LOGS.TO")} <b> ${this.datePipe.transform(
                    this.endDateTime,
                    `${BASE_CONSTANTS.TCI_DATE_TIME_FORMAT}`
                )}`;
            }
        }

        this.logsSummary += "</b>";
    }

    /**
     * Replace the colon that's between the second and the millisecond with a dot in order to make the date time format
     * conform to ISO 8601.
     *
     * @param {string} unityDateTimeString
     * @returns {string}
     */
    private correctDateTimeString(unityDateTimeString: string): number {
        const momentObj = moment(unityDateTimeString, "YYYY-MMM-DD HH:mm:ss:SSS");

        return momentObj.isValid() ?
            parseInt(momentObj.format("x"), 10) :
            NaN;
    }

    private createQuery(
        queryStyle: QUERY_STYLES,
        timeRangeId?: TIME_RANGE_IDS,
        startTime?: number,
        endTime?: number,
        limitMaximumRows?: number
    ): Observable<IUnityProxyQueryResponse> {
        const input: IUnityProxyLogsQueryRequest = {
            appId: this.appId,
            style: queryStyle,
            timeSlot: timeRangeId || undefined,
            startTime: startTime && startTime.toString() || undefined,
            endTime: endTime && endTime.toString() || undefined,
            limitMaximumRows: this.tciLogLimitUI
        };

        if (this.logCategory === LOG_CATEGORIES.buildtime) {
            input.appId = "buildserver, orchestrator";
            input.messageTransform = `trim(substring(msg, find(msg, 'appIdUIDecoratedLog=${this.appId}') + len('appIdUIDecoratedLog=${this.appId}'), len(msg))) as logentry`;
            input.sysBodyIndex = ` | sys_body CONTAINS 'appIdUIDecoratedLog=${this.appId}'`;
        } else {
            input.logLevels = lodash
                .chain(this.filterLogLevels)
                .map((logLevel: IUnityProxyLogLevel) => {
                    return logLevel.value && logLevel.id;
                })
                .compact() // Call .compact() to eliminate any `undefined` values in the array.
                .value();
        }

        this.historicalQueryTries = 1;

        return this.logsService.createQuery(input);
    }

    private createQueryForDownload(
        timeRangeId?: TIME_RANGE_IDS,
        startTime?: number,
        endTime?: number,
        limitMaximumRows?: number
    ): Observable<IUnityProxyQueryResponse> {
        const input: IUnityProxyLogsQueryRequest = {
            appId: this.appId,
            style: QUERY_STYLES.history,
            timeSlot: timeRangeId || undefined,
            startTime: startTime && startTime.toString() || undefined,
            endTime: endTime && endTime.toString() || undefined,
            limitMaximumRows: this.tciLogLimitDownload
        };

        if (this.logCategory === LOG_CATEGORIES.buildtime) {
            input.appId = "buildserver, orchestrator";
            input.messageTransform = `trim(substring(msg, find(msg, 'appIdUIDecoratedLog=${this.appId}') + len('appIdUIDecoratedLog=${this.appId}'), len(msg))) as logentry`;
            input.sysBodyIndex = ` | sys_body CONTAINS 'appIdUIDecoratedLog=${this.appId}'`;
        } else {
            input.logLevels = lodash
                .chain(this.filterLogLevels)
                .map((logLevel: IUnityProxyLogLevel) => {
                    return logLevel.value && logLevel.id;
                })
                .compact() // Call .compact() to eliminate any `undefined` values in the array.
                .value();
        }

        return this.logsService.createQueryForDownload(input);
    }

    private doUnsubscribe(subscriptions: { [key: string]: Subscription }, subscriptionKeys: SUBSCRIPTION_KEYS[]): void {
        lodash.forEach(subscriptionKeys, ((subscriptionKey: SUBSCRIPTION_KEYS) => {
            const subscription: Subscription = subscriptions[subscriptionKey];
            if (subscription) {
                subscription.unsubscribe();
                delete subscriptions[subscriptionKey];
            }
        }));
    }

    private fetchHistoricalLogs(
        queryId: string,
        startRow: number,
        fetchSize: number
    ): void {
        this.logsFetchAttempts++;
        this.historicalSubscriptions[SUBSCRIPTION_KEYS.fetchLogs] = this
            .logsService
            .fetchLogs({
                queryId,
                style: QUERY_STYLES.history,
                startRow,
                fetchSize
            })
            .subscribe(
                (logResponse: IUnityProxyFetchHistoricalResponse) => {
                    if (
                        logResponse.code === "200" &&
                        !lodash.isUndefined(logResponse.content) &&
                        (lodash.isUndefined(logResponse.content.finished) ||
                            lodash.isEqual(logResponse.content.finished, "true"))
                    ) {
                        this.logsFetchAttempts = 0;
                        this.historicalLogsCount = logResponse.content.totalCount;
                        this.noLogs = this.historicalLogsCount === 0;
                        if (logResponse.content.results.length === 0) {
                            this.isLoadingLogs = false;
                            this.isLazyLoading = false;
                        }
                        this.insertNewLogs(logResponse, QUERY_STYLES.history);
                    } else {
                        this.unsubscribeHistorical([
                            SUBSCRIPTION_KEYS.fetchLogs
                        ]);
                        this.historicalSubscriptions[SUBSCRIPTION_KEYS.delayedFetchLogs] = Observable
                            .timer(BaseAppDetailLogsComponent.DEFAULT_HISTORICAL_LOGS_INTERVAL)
                            .subscribe(
                                () => {
                                    this.fetchHistoricalLogs(queryId, startRow, fetchSize);
                                },
                                error => {
                                    // delete this.subscriptions[SUBSCRIPTION_KEYS.delayedFetchLogs];
                                }
                            );
                    }
                },
                error => {
                    this.isLoadingLogs = false;
                    this.isLazyLoading = false;
                    console.log(error);
                }
            );
    }

    private fetchLogs(
        queryId: string,
        style: QUERY_STYLES,
        startRow: number,
        fetchSize: number
    ): void {
        this.isLoadingLogs = true;
        this.isLazyLoading = true;
        if (style === QUERY_STYLES.history) {
            this.fetchHistoricalLogs(queryId, startRow, fetchSize);
        } else {
            this.fetchRealtimeLogs(queryId, startRow, fetchSize);
        }
    }

    private fetchLogsForDownload(
        queryId: string,
        startRow: number,
        fetchSize: number
    ): void {
    /**
     * To implement the progress bar for downloading, changed the query style to QUERY_STYLES.history
     * In QUERY_STYLES.tail, it is not possible to calucate the percentage,
     * And the legacy way is to show the downloaded size of the logs like [15 kb] in 1.x
     */
        this.historicalSubscriptions[SUBSCRIPTION_KEYS.fetchLogsForDownload] = this
            .logsService
            .fetchLogsForDownload({
                queryId,
                style: QUERY_STYLES.history,
                startRow,
                fetchSize
            })
            .subscribe(
                (logResponse: IUnityProxyFetchHistoricalResponse) => {
                    if (
                        !lodash.isUndefined(logResponse.content) &&
                        lodash.isArray(logResponse.content.results)
                    ) {
                        this.downloadSize = this.getLogsContentForDownload(logResponse);

                        // To make sure download should be canceled on closing the download dialog.
                        if (this.downloadDialogOpen) {
                            if (logResponse.content.totalCount > 0) {
                                startRow += this.tciLogDownloadFetchSize;
                                this.downloadPercentage = parseInt(((startRow / logResponse.content.totalCount) * 100).toFixed(0), 10);
                                this.downloadStatusMsg = this.translateService.instant("NG_APP_LOGS.PREPARING_DOWNLOAD");
                            }
                            if (logResponse.content.totalCount === -1 || startRow < logResponse.content.totalCount) {
                                if (
                                    this.downloadSize.split(" ")[1] === this.tciLogDownloadSizeLimit.split(" ")[1] &&
                                    parseFloat(this.downloadSize.split(" ")[0]) >= parseFloat(this.tciLogDownloadSizeLimit.split(" ")[0])
                                ) {
                                    console.log("Download limit reached");
                                    this.toastr.warn(this.translateService.instant("NG_APP_LOGS.LIMIT_REACHED", {
                                        tciLogDownloadSizeLimit: `${this.tciLogDownloadSizeLimit}`
                                    }));

                                    this.finalizeDownload();
                                } else {
                                    this.fetchLogsForDownload(queryId, startRow, fetchSize);
                                }
                            } else {
                                if (logResponse.content.totalCount === this.tciLogLimitDownload) {
                                    this.toastr.warn(this.translateService.instant("NG_APP_LOGS.LOGS_LIMIT_REACHED", {tciLogLimitDownload: `${this.tciLogLimitDownload}`}));
                                }
                                this.finalizeDownload();
                            }
                        } else {
                            this.resetDownloadStatus();
                        }
                    } else {
                        this.resetDownloadStatus();
                    }
                },
                error => {
                    this.resetDownloadStatus();
                    // delete this.subscriptions[SUBSCRIPTION_KEYS.fetchLogsForDownload];
                    console.log(error);
                }
            );
    }

    private fetchRealtimeLogs(
        queryId: string,
        startRow: number,
        fetchSize: number
    ): void {
        this.realtimeSubscriptions[SUBSCRIPTION_KEYS.fetchLogs] = this
            .logsService
            .fetchLogs({
                queryId,
                style: QUERY_STYLES.tail,
                startRow,
                fetchSize
            })
            .subscribe(
                (logResponse: IUnityProxyFetchTailResponse) => {
                    if (
                        !lodash.isUndefined(logResponse.content) &&
                        lodash.isArray(logResponse.content.results)
                    ) {
                        this.insertNewLogs(logResponse, QUERY_STYLES.tail);
                        this.realtimeSubscriptions[SUBSCRIPTION_KEYS.delayedFetchLogs] = Observable
                            .timer(BaseAppDetailLogsComponent.DEFAULT_REALTIME_LOGS_INTERVAL)
                            .subscribe(
                                () => {
                                    this.fetchRealtimeLogs(queryId, startRow, fetchSize);
                                },
                                error => {
                                    // delete this.subscriptions[SUBSCRIPTION_KEYS.delayedFetchLogs];
                                }
                            );
                    }
                },
                error => {
                    this.isLoadingLogs = false;
                    this.isLazyLoading = false;
                    // delete this.subscriptions[SUBSCRIPTION_KEYS.fetchLogs];
                    console.log(error);
                }
            );
    }

    private filterByKeyword(logEntries: IUnityProxyLogEntry[]): IUnityProxyLogEntry[] {
        let entries: IUnityProxyLogEntry[] = [];

        if (this.keywordFilter) {
            entries = logEntries.filter((item: IUnityProxyLogEntry) => {
                return this.shouldBeKept(item.message);
            });
        } else if (this.keywordFilter === undefined || this.keywordFilter === "") {
            entries = logEntries;
        }

        this.numberOfDisplayed.displayed = entries.length;
        this.noLogs = this.numberOfDisplayed.displayed === 0;

        return entries;
    }

    private finalizeDownload(): void {
        if (this.logsContentForDownload.length > 0) {
            console.log("There are logs for download");
            const a = window.document.createElement("a");
            a.href = window.URL.createObjectURL(new Blob([this.logsContentForDownload.join("")], {
                type: "text/plain"
            }));
            let namePart = this.selectedTimeRangeType === "fixed" ?
                this.selectedFixedTimeRange.label :
                `${this.datePipe.transform(this.startDateTime, "MMddyyyy")}-${this.datePipe.transform(
                    this.endDateTime,
                    "MMddyyyy"
                )}`;
            namePart += ("-" + this.logCategory);
            a.download = this.input.application.name + "-" + namePart + ".log";
            console.log("Downloaded file name is ", a.download);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } else {
            this
                .toastr
                .info(
                    this
                        .translateService
                        .instant("NG_APP_LOGS.NO_LOGS_TO_DOWNLOAD")
                );
        }
        this.resetDownloadStatus();
        this.logsContentForDownload = [];
        this.downloadSize = "";
        this.downloadDialogOpen = false;
    }

    private findIndex(logEntry: IUnityProxyLogEntry): number {
        return this.logEntries.findIndex((item: IUnityProxyLogEntry) => item.id === logEntry.id);
    }

    private findInLogEntries(): void {
        this.isLoadingLogs = true;
        this.logEntries = this.filterByKeyword(this.origLogEntries.slice());
        this.isLoadingLogs = false;
    }

    private fireEndReachedEvent(): void {
        this.infiniteScrollingSubject.next();
    }

    private getCustomLogs(): void {
        const startTime = this.startDateTime.getTime();
        const endTime = this.endDateTime.getTime();

        if (startTime <= endTime) {
            this.queryFetchLogs({
                style: QUERY_STYLES.history,
                startTime,
                endTime
            });
        } else {
            this.toastr.clear();
            this.toastr.error(this.translateService.instant("NG_APP_LOGS.CUSTOM_TIME_RANGE_ERROR"));
        }
    }

    private getHistoricalLogs(): void {
        const timeRangeId = this.timeRangeFilter ?
            this.timeRangeFilter.id :
            BaseAppDetailLogsComponent.DEFAULT_TIME_RANGE_ID;

        // this.constructLogsSummary(timeRangeId);

        if (this.lastTimeRangeId !== timeRangeId) {
            this.page = 0;
        }

        this.newMessagesCount = 0;
        this.logsListScrolled = false;
        this.showLogNotification = false;

        if (this.lastTimeRangeId !== TIME_RANGE_IDS.custom) {
            this.queryFetchLogs({
                style: QUERY_STYLES.history,
                timeRangeId
            });
        }
    }

    private getLogsContentForDownload(logsResponse: IUnityProxyFetchHistoricalResponse): string {
        if (logsResponse.content.results.length > 0) {
            logsResponse
                .content
                .results
                .forEach((value: string[]) => {
                    if (this.logCategory === LOG_CATEGORIES.buildtime) {
                        value = this.logsService.removeExtraLogLevelMessages(value);
                        if (this.logLevelIsNotFiltered(value[3])) {
                            this.logsContentForDownload.push(value[2]);
                            this.logsContentForDownload.push(" ");
                            this.logsContentForDownload.push(value[3]);
                            this.logsContentForDownload.push(" ");
                            this.logsContentForDownload.push(value[4]);
                            this.logsContentForDownload.push("\n");
                        }
                    } else {
                        this.logsContentForDownload.push(value[2]);
                        this.logsContentForDownload.push(" ");
                        this.logsContentForDownload.push(value[3]);
                        this.logsContentForDownload.push(" ");
                        this.logsContentForDownload.push(value[4]);
                        this.logsContentForDownload.push("\n");
                    }
                });
        }
        return this.sizeof(this.logsContentForDownload.join(""));
    }

    private getRealtimeLogs(): void {
        this.newMessagesCount = 0;
        this.logsListScrolled = false;
        this.showLogNotification = false;
        this.queryFetchLogs({
            style: QUERY_STYLES.tail
        });
        /* This is to get last 1 hour logs in case the log viewer is empty which may make the user feel strange */
        this.queryFetchLogs(
            {
                style: QUERY_STYLES.history,
                timeRangeId: TIME_RANGE_IDS["1h"]
            },
            false
        );
    }

    private getTimeRangeId(): string {
        const timeRange: IUnityProxyTimeRange = lodash.find(this.filterTimeRange, {
            id: this.timeRangeFilter.id
        });
        const timeRangeId: string = timeRange && timeRange.id;

        return timeRangeId || BaseAppDetailLogsComponent.DEFAULT_TIME_RANGE_ID;
    }

    private insertNewLogs(
        logsResponse: IUnityProxyFetchTailResponse | IUnityProxyFetchHistoricalResponse,
        queryStyle: QUERY_STYLES
    ): void {
        logsResponse
            .content
            .results
            .forEach((value: string[]) => {

                if (this.logCategory === LOG_CATEGORIES.buildtime) {
                    value = this.logsService.removeExtraLogLevelMessages(value);
                }

                const logEntry: IUnityProxyLogEntry = {
                    id: value[0],
                    time: this.correctDateTimeString(value[2]) || undefined,
                    logLevelId: <LOG_LEVEL_IDS>value[3],
                    message: value[4]
                };
                const hasTheLogLevelToShow = lodash.filter(
                    this.filterLogLevels,
                    (logLevel: IUnityProxyLogLevel) => {
                        return logLevel.value &&
                            logLevel.status === "active" &&
                            logLevel.id === logEntry.logLevelId;
                    }
                ).length > 0 || logEntry.logLevelId === "N/A";
                const isDuplicate = lodash
                    .chain(this.origLogEntries)
                    .map((origLogEntry: IUnityProxyLogEntry) => {
                        return origLogEntry.id;
                    })
                    .includes(logEntry.id)
                    .value();

                if (!isDuplicate && (this.showingAllLogLevels() || hasTheLogLevelToShow)) {
                    if (queryStyle === QUERY_STYLES.tail) {
                        // Latest logs first
                        this.pushItem(logEntry, true);
                        if (this.logsListScrolled) {
                            this.newMessagesCount++;
                        }
                    } else {
                        // Latest logs at the bottom
                        this.pushItem(logEntry);
                    }
                }
            });

        this.numberOfDisplayed = {
            displayed: this.logEntries.length,
            total: this.origLogEntries.length
        };

        if (logsResponse.content.results.length > 0) {
            this.isLoadingLogs = false;
            this.isLazyLoading = false;
        }
    }

    private keyupHandler($event): void {
        const nKeycode = $event.which;

        if (nKeycode === 13) {
            // The Enter key
            this.findInLogEntries();
        } else if (nKeycode === 27) {
            // The ESC key
            this.clearFilter();
        }
    }

    /**
     * Load logs when the user scrolls up to the top of the currently shown logs or when the "Load more logs" button is
     * clicked.
     *
     * @author Kevin Li <huali@tibco-support.com>
     */
    private lazyLoadLogs(): void {
        if (!this.isLazyLoading) {
            this.isLazyLoading = true;
            if (!this.initialized) {
                this.initialize();
            } else {
                if (!this.noMoreLogs) {
                    // let fetchSize = BaseAppDetailLogsComponent.LOGS_PER_PAGE;

                    // if (this.historicalLogsCount > BaseAppDetailLogsComponent.LOGS_PER_PAGE) { // There are more than 1 page of logs to be fetched.
                    //     fetchSize = BaseAppDetailLogsComponent.LOGS_PER_PAGE;
                    //     this.prevOffsetCount = this.prevOffsetCount + BaseAppDetailLogsComponent.LOGS_PER_PAGE;
                    // } else { // There's less than 1 page of logs to be fetched.
                    //     fetchSize = this.historicalLogsCount;
                    //     this.prevOffsetCount = this.historicalLogsCount;
                    // }

                    if (this.historicalQuery) { // There is an existing historical query.
                        if (this.historicalLogsCount > BaseAppDetailLogsComponent.LOGS_PER_PAGE * (this.page + 1)) { // There are more historical logs to fetch.
                            this.page++;
                            this.fetchLogs(
                                this.historicalQuery.content.queryId,
                                QUERY_STYLES.history,
                                this.page * BaseAppDetailLogsComponent.LOGS_PER_PAGE,
                                BaseAppDetailLogsComponent.LOGS_PER_PAGE
                            );
                        } else { // No more logs to fetch.
                            if (this.historicalLogsCount >= this.tciLogLimitUI) {
                                this.toastr.info(this.translateService.instant("NG_APP_LOGS.TCI_LOG_LIMIT_UI", {tciLogLimitUI: `${this.tciLogLimitUI}`}));
                            } else {
                                this.noMoreLogs = true;
                                this.toastr.info(this.translateService.instant("NG_APP_LOGS.NO_MORE_LOGS"));
                            }
                        }
                    // } else { // When log viewer is in realtime mode and this method has not been called yet.
                    //     let startTime: number, endTime: number;
                    //     if (this.timeRangeFilter.id === TIME_RANGE_IDS.custom) { // "Custom" is selected in the time range filter.
                    //         if (this.realtimeLogStartTime.getTime() < this.startDateTime.getTime()) { // Realtime logs started to fetch before the start time of the time range filter.
                    //             this.noMoreLogs = true;
                    //         } else { // Realtime logs started to fetch between the start time and end time of the time range filter or after the end time
                    //             // of the time range filter.
                    //             startTime = this.startDateTime.getTime();
                    //             endTime = this.realtimeLogStartTime.getTime();
                    //         }
                    //     }
                    //     this.queryFetchLogs(
                    //         {
                    //             style: QUERY_STYLES.history,
                    //             timeRangeId: this.timeRangeFilter.id,
                    //             startTime,
                    //             endTime
                    //         },
                    //         false
                    //     );
                    }
                }
            }
        }
    }

    public logLevelIsNotFiltered(logLevelId: string): boolean {
        return !lodash.reduce(this.filterLogLevels, (accumulator: boolean, filterLogLevel: IUnityProxyLogLevel) => {
            return filterLogLevel.value && accumulator;
        }, false) || lodash.get(lodash.find(this.filterLogLevels, {
            id: logLevelId
        }), "value");
    }

    private openDownloadDialog(): void {
        this.downloadDialogOpen = true;
    }

    private pushItem(
        item: IUnityProxyLogEntry,
        fromBeginning: boolean = false
    ): void {
        if (fromBeginning) {
            this.origLogEntries.unshift(item);
            if (this.shouldBeKept(item.message)) {
                this.logEntries.unshift(item);
            }
        } else {
            this.origLogEntries.push(item);
            if (this.shouldBeKept(item.message)) {
                this.logEntries.push(item);
            }
        }
    }

    private queryByCustomTimeRange(): void {
        // this.switchLogMode(LOG_MODES.historical);
        this.unsubscribeAll();
        this.getCustomLogs();
        this.constructLogsSummary(TIME_RANGE_IDS.custom);
    }

    private queryByLogLevel(): void {
        this.updateFilterLogLevelSelections();
        this.unsubscribeAll();

        if (this.logMode === LOG_MODES.realtime_active) {
            this.getRealtimeLogs();
        } else if (this.logMode === LOG_MODES.realtime_inactive) {
            // TODO: decide what to do for this mode
        } else if (this.logMode === LOG_MODES.historical) {
            if (this.isCustomTimeRange) {
                this.getCustomLogs();
                this.constructLogsSummary(TIME_RANGE_IDS.custom);
            } else {
                this.getHistoricalLogs();
            }
        }
    }

    private queryByFixedTimeRange(timeRange: IUnityProxyTimeRange): void {
        this.timeRangeFilter = timeRange;
        this.wasCustomTimeRange = this.isCustomTimeRange;
        this.isCustomTimeRange = timeRange.id === TIME_RANGE_IDS.custom;
        this.switchLogMode(LOG_MODES.historical);
        this.constructLogsSummary(timeRange.id);

        if (!this.isCustomTimeRange) {
            this.getHistoricalLogs();
        } else {
            this.resetTimeRange();
        }
    }

    private queryFetchLogs(
        input: {
            style: QUERY_STYLES;
            timeRangeId?: TIME_RANGE_IDS;
            startTime?: number;
            endTime?: number;
        },
        shouldClearItems: boolean = true
    ): void {
        this.lastTimeRangeId = input.timeRangeId;
        this.noMoreLogs = false;
        this.queryId = undefined;
        this.isLoadingLogs = true;

        let subscriptionsCache = this.historicalSubscriptions;

        if (input.style === QUERY_STYLES.tail) {
            this.realtimeLogStartTime = new Date();
            subscriptionsCache = this.realtimeSubscriptions;
        }

        if (shouldClearItems) {
            this.clearItems();
        }

        subscriptionsCache[SUBSCRIPTION_KEYS.createQuery] = this
            .createQuery(input.style, input.timeRangeId, input.startTime, input.endTime, this.tciLogLimitUI)
            .subscribe(
                (query: IUnityProxyQueryResponse) => {
                    this.queryId = query.content.queryId;
                    if (input.style === QUERY_STYLES.history) {
                        this.historicalQuery = query;
                    } else if (input.style === QUERY_STYLES.tail) {
                        this.realtimeQuery = query;
                        this.loadingRealTime = true;
                        this.isLoadingLogs = false;
                        this.isLazyLoading = false;
                    }
                    this.fetchLogs(
                        this.queryId,
                        input.style,
                        this.page * this.fetchSize,
                        this.fetchSize
                    );
                },
                error => {
                    this.isLoadingLogs = false;
                    this.isLazyLoading = false;
                    console.log(error);
                },
                () => {
                    // delete this.subscriptions[SUBSCRIPTION_KEYS.createQuery];
                }
            );
    }

    private queryFetchLogsForDownload(
        input: {
            timeRangeId?: TIME_RANGE_IDS;
            startTime?: number;
            endTime?: number;
        }
    ): void {
        this.isLoadingLogs = true;
        this.isPreparingDownload = true;

        this.historicalSubscriptions[SUBSCRIPTION_KEYS.createQueryForDownload] = this
            .createQueryForDownload(input.timeRangeId, input.startTime, input.endTime, this.tciLogLimitDownload)
            .subscribe(
                (query: IUnityProxyQueryResponse) => {
                    this.fetchLogsForDownload(
                        query.content.queryId,
                        0,
                        this.tciLogDownloadFetchSize
                    );
                },
                error => {
                    this.resetDownloadStatus();
                    console.log(error);
                },
                () => {
                    // delete this.subscriptions[SUBSCRIPTION_KEYS.createQueryForDownload];
                }
            );
    }

    private resetTimeRange(): void {
        this.endDateTime = new Date();
        this.startDateTime = new Date(this.endDateTime.getTime() - 24 * 60 * 60 * 1000);
    }

    private scrollLogsListToTop(): void {
        BaseAppDetailLogsComponent.logsListElement.scrollTop = 0;
        this.logsListScrolled = false;
        this.newMessagesCount = 0;
    }

    private setAllLevelsFalse(): void {
        lodash.forEach(this.filterLogLevels, (logLevel: IUnityProxyLogLevel) => {
            logLevel.value = false;
        });
    }

    private shouldBeKept(sInput: string): boolean {
        return this.keywordFilter ?
            sInput
                .toLowerCase()
                .search(lodash.escapeRegExp(this.keywordFilter.toLowerCase())) > -1 :
            true;
    }

    private showingAllLogLevels(): boolean {
        const allLevelsSelected = lodash.every(this.filterLogLevels, (logLevel: IUnityProxyLogLevel) => logLevel.value);
        const noLevelSelected = lodash.every(this.filterLogLevels, (logLevel: IUnityProxyLogLevel) => !logLevel.value);

        // Logs of all levels will be shown if all log levels are selected or none of them is selected
        return allLevelsSelected || noLevelSelected;
    }

    private showLess(event): void {
        event.target.parentElement.classList.remove("expanded");
    }

    private showMore(event): void {
        event.target.parentElement.classList.add("expanded");
    }

    private sizeof(logs: string): string {

        const objectList: any[] = [];
        const stack: string[] = [logs];
        let bytes = 0;

        while (stack.length) {
            const item: any = stack.pop();

            if (lodash.isBoolean(item)) {
                bytes += 4;
            } else if (lodash.isString(item)) {
                bytes += item.length * 2;
            } else if (lodash.isNumber(item)) {
                bytes += 8;
            } else if (lodash.isPlainObject(item) && !lodash.includes(objectList, item)) {
                objectList.push(item);
                stack.push(...lodash.values(item));
            }
        }

        return this.bytesToSize(bytes / 2, 1);
    }

    private startDownload(): void {
        let input;

        switch (this.selectedTimeRangeType) {
            case "fixed":
                input = {
                    style: QUERY_STYLES.tail,
                    timeRangeId: this.selectedFixedTimeRange.id
                };
                break;
            case "custom":
                input = {
                    style: QUERY_STYLES.history,
                    startTime: this.startDateTime.getTime(),
                    endTime: this.endDateTime.getTime()
                };
                break;
            default:
                return;
        }

        this.queryFetchLogsForDownload(input);
    }

    private resetDownloadStatus() {
        this.isLoadingLogs = false;
        this.isLazyLoading = false;
        this.isPreparingDownload = false;
        this.downloadPercentage = 0;
        this.logsContentForDownload = [];
        this.downloadStatusMsg = this.translateService.instant("NG_APP_LOGS.INITIATING_DOWNLOAD");
    }
    private switchLogMode(logMode: LOG_MODES): void {
        const oldLogMode = this.logMode;
        this.logMode = logMode;
        this.isRealtimeActive = this.logMode === LOG_MODES.realtime_active;
        this.page = 0;
        this.historicalQuery = null;
        this.realtimeQuery = null;
        this.historicalLogsCount = 0;
        this.noMoreLogs = false;
        this.unsubscribeAll();

        if (!this.wasCustomTimeRange && this.isCustomTimeRange) {
            this.clearItems();
            this.isLoadingLogs = false;
            this.isLazyLoading = false;
        }
    }

    private switchToRawView(): void {
        this.logViewStyle = LOG_VIEW_STYLES.raw;
    }

    private switchToTableView(): void {
        this.logViewStyle = LOG_VIEW_STYLES.table;
    }

    private switchToggle(): void {
        this.switchToggleData.isOn = this.isRealtimeActive;
        this.switchToggleData.switchToggleClass = this.switchToggleData.isOn ? "switch-on" : "";
    }

    private toggleRawView(event): void {
        if (event.value === LOG_VIEW_STYLES.raw) {
            this.switchToRawView();
        } else {
            this.switchToTableView();
        }
    }

    private toggleLogCategory(sLogCategory: LOG_CATEGORIES): void {
        if (this.logCategory !== sLogCategory) {
            this.logCategory = sLogCategory;

            if (sLogCategory === "buildtime") {
                this.activateHistorical(TIME_RANGE_MEASURES["30m"]);
            } else if (sLogCategory === "runtime") {
                this.isRealtimeActive = false;
                // this.toggleRealtime();
            }
        }
    }

    private toggleRealtime(event): void {
        this.isCustomTimeRange = false;
        if (event.checked) {
            this.switchLogMode(LOG_MODES.realtime_active);
            this.getRealtimeLogs();
        } else {
            this.switchLogMode(LOG_MODES.realtime_inactive);
            /* IPAS-14349: Setting default time range filter to 5 min when toggle off real time mode. */
            this.timeRangeFilter = lodash.find(this.filterTimeRange, {
                id: TIME_RANGE_IDS["5m"]
            });
            this.queryByFixedTimeRange(this.timeRangeFilter);
            this.isLoadingLogs = false;
        }
    }

    private tooltip(timeRangeId: TIME_RANGE_IDS) {
        if (timeRangeId === TIME_RANGE_IDS.custom) {
            return this.translateService.instant(BaseAppDetailLogsComponent.LANG_LOG +
                TIME_RANGE_IDS.custom.toUpperCase());
        } else {
            const timeRange = lodash.find(this.filterTimeRange, {
                id: timeRangeId
            });
            return `${this.translateService.instant(BaseAppDetailLogsComponent.LANG_LOG +
                BaseAppDetailLogsComponent.LAST)} ${timeRange.label}`;
        }
    }

    private trackByLogEntryId(index: number, logEntry: IUnityProxyLogEntry): string {
        return logEntry.id;
    }

    private unsubscribeAll(): void {
        const subscriptionKeys = [
            SUBSCRIPTION_KEYS.createQuery,
            SUBSCRIPTION_KEYS.createQueryForDownload,
            SUBSCRIPTION_KEYS.fetchLogs,
            SUBSCRIPTION_KEYS.fetchLogsForDownload,
            SUBSCRIPTION_KEYS.delayedFetchLogs
        ];
        this.unsubscribeRealtime(subscriptionKeys);
        this.unsubscribeHistorical(subscriptionKeys);
    }

    private unsubscribeHistorical(subscriptionKeys: SUBSCRIPTION_KEYS[]): void {
        if (subscriptionKeys && subscriptionKeys.length > 0) {
            this.doUnsubscribe(this.historicalSubscriptions, subscriptionKeys);
        }
    }

    private unsubscribeRealtime(subscriptionKeys: SUBSCRIPTION_KEYS[]): void {
        if (subscriptionKeys && subscriptionKeys.length > 0) {
            this.doUnsubscribe(this.realtimeSubscriptions, subscriptionKeys);
        }
    }

    private updateFilterLogLevelSelections(): void {
        lodash.forEach(this.filterLogLevels, (logLevel: IUnityProxyLogLevel) => {
            logLevel.value = lodash.includes(this.selectedLogLevels, logLevel.id);
        });
    }

    private updateFilterLogLevelIcons(): void {
        const allInactive = lodash.every(this.filterLogLevels, (log: IUnityProxyLogLevel) => !log.value);

        this.filterLogLevels.forEach(function (log: IUnityProxyLogLevel) {
            if (!log.value && !allInactive) {
                log.status = "inactive";
            } else {
                log.status = "active";
            }
        });
    }
}
